import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export type AuditCategory =
  | 'alarm'       // acknowledge, close, escalate
  | 'access'      // door command, credential change
  | 'user'        // login, logout, create, modify, delete
  | 'config'      // settings change, integration config
  | 'report'      // report run, export
  | 'job'         // job created, updated, closed
  | 'incident'    // incident created, updated, closed
  | 'asset'       // asset add, modify, decommission
  | 'system';     // health events, WS connect/disconnect

export interface AuditEntry {
  id: string;
  ts: number;           // epoch ms
  category: AuditCategory;
  action: string;       // human-readable verb phrase
  operator: string;     // email or 'system'
  site?: string;
  target?: string;      // object acted on (alarm ID, door name, etc.)
  detail?: string;      // extra context
  before?: string;      // for config changes
  after?: string;
  severity?: 'info' | 'warning' | 'critical';
}

// ─── Context ──────────────────────────────────────────────────────────────────

interface AuditCtxValue {
  entries: AuditEntry[];
  log: (entry: Omit<AuditEntry, 'id' | 'ts'>) => void;
  clear: () => void;
  loaded: boolean;
}

const AuditCtx = createContext<AuditCtxValue>({
  entries: [],
  log: () => {},
  clear: () => {},
  loaded: false,
});

// ─── Seed entries ─────────────────────────────────────────────────────────────

const now = Date.now();
const SEED_ENTRIES: AuditEntry[] = [
  { id:'AU001', ts: now - 120000,   category:'alarm',  action:'Alarm acknowledged',        operator:'j.smith@lemai.ai',     site:'Town Hall',        target:'ALM-1042 Duress Button',        severity:'critical' },
  { id:'AU002', ts: now - 300000,   category:'access', action:'Door unlocked remotely',     operator:'j.smith@lemai.ai',     site:'Civic Centre',     target:'Server Room Door',              severity:'warning' },
  { id:'AU003', ts: now - 600000,   category:'user',   action:'User login',                 operator:'m.jones@lemai.ai',     site:'All Sites',        target:'m.jones@lemai.ai',              severity:'info' },
  { id:'AU004', ts: now - 900000,   category:'config', action:'Integration edited',         operator:'admin@lemai.ai',       site:'All Sites',        target:'Inner Range Integriti',         before:'Offline', after:'Online', severity:'info' },
  { id:'AU005', ts: now - 1200000,  category:'report', action:'Report exported (CSV)',      operator:'m.jones@lemai.ai',     site:'All Sites',        target:'Monthly Incident Summary',      severity:'info' },
  { id:'AU006', ts: now - 1800000,  category:'alarm',  action:'Alarm escalated',            operator:'system',               site:'Civic Centre',     target:'ALM-1039 Door Forced Open',     severity:'critical' },
  { id:'AU007', ts: now - 2400000,  category:'job',    action:'Job created',                operator:'j.smith@lemai.ai',     site:'Aquatic Centre',   target:'JOB-20260413-0001',             severity:'info' },
  { id:'AU008', ts: now - 3000000,  category:'asset',  action:'Asset marked faulty',        operator:'admin@lemai.ai',       site:'Aquatic Centre',   target:'A005 PTZ Camera Car Park',      severity:'warning' },
  { id:'AU009', ts: now - 3600000,  category:'user',   action:'User created',               operator:'admin@lemai.ai',       site:'All Sites',        target:'t.wilson@lemai.ai',             severity:'info' },
  { id:'AU010', ts: now - 7200000,  category:'system', action:'WebSocket connection lost',  operator:'system',               site:'All Sites',        target:'Alarm stream',                  severity:'warning' },
  { id:'AU011', ts: now - 7500000,  category:'system', action:'WebSocket reconnected',      operator:'system',               site:'All Sites',        target:'Alarm stream',                  severity:'info' },
  { id:'AU012', ts: now - 86400000, category:'user',   action:'User login',                 operator:'admin@lemai.ai',       site:'All Sites',        target:'admin@lemai.ai',                severity:'info' },
];

let _seq = 100;
function nextId() { return `AU-${Date.now()}-${++_seq}`; }

function apiPostAudit(entry: Omit<AuditEntry, 'id' | 'ts'>) {
  const token = localStorage.getItem('lemai_token');
  if (!token) return;
  fetch('/api/audit', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(entry),
  }).catch(() => {});
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AuditProvider({ children }: { children: React.ReactNode }) {
  const [entries, setEntries] = useState<AuditEntry[]>(SEED_ENTRIES);
  const [loaded, setLoaded] = useState(false);

  // Load persisted audit entries from backend on mount
  useEffect(() => {
    const token = localStorage.getItem('lemai_token');
    if (!token) return;
    fetch('/api/audit?limit=500', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then((data: AuditEntry[] | null) => {
        if (data && data.length > 0) setEntries(data);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const log = useCallback((entry: Omit<AuditEntry, 'id' | 'ts'>) => {
    const full: AuditEntry = { id: nextId(), ts: Date.now(), ...entry };
    setEntries(prev => [full, ...prev].slice(0, 2000));
    apiPostAudit(entry);   // persist to backend
  }, []);

  const clear = useCallback(() => setEntries([]), []);

  return (
    <AuditCtx.Provider value={{ entries, log, clear, loaded }}>
      {children}
    </AuditCtx.Provider>
  );
}

export function useAudit() { return useContext(AuditCtx); }

// ─── Helper formatters ────────────────────────────────────────────────────────

export function fmtAuditTs(ts: number): string {
  const d = new Date(ts);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  if (isToday) return d.toLocaleTimeString('en-AU', { hour:'2-digit', minute:'2-digit', second:'2-digit' });
  return d.toLocaleString('en-AU', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' });
}

export const AUDIT_CATEGORY_COLOR: Record<AuditCategory, string> = {
  alarm:    '#ef4444',
  access:   '#3b82f6',
  user:     '#22c55e',
  config:   '#f59e0b',
  report:   '#06b6d4',
  job:      '#a855f7',
  incident: '#f97316',
  asset:    '#555555',
  system:   '#8b5cf6',
};

export const AUDIT_SEVERITY_COLOR: Record<string, string> = {
  info:     '#22c55e',
  warning:  '#f59e0b',
  critical: '#ef4444',
};
